/* PDP only. No DOM work outside product-view and no product-data writes. */
(() => {
  const key = options => JSON.stringify(options);
  const normalized = value => String(value || '').trim().toLowerCase();

  class VariantMap {
    constructor(variants, total, optionCount) {
      this.variants = variants;
      this.byId = new Map();
      this.byOptions = new Map();
      for (const variant of variants) {
        if (!variant.id || !Array.isArray(variant.options) || variant.options.length !== optionCount ||
            typeof variant.available !== 'boolean' || !Number.isFinite(variant.price) ||
            typeof variant.priceText !== 'string' || this.byId.has(String(variant.id)) ||
            this.byOptions.has(key(variant.options))) throw Error('Incomplete variant data');
        this.byId.set(String(variant.id), variant);
        this.byOptions.set(key(variant.options), variant);
      }
      if (!Number.isInteger(total) || total < 1 || this.byId.size !== total) throw Error('Incomplete variant data');
      this.mainIds = new Set(variants.map(v => String(v.mediaId || '')).filter(Boolean));
    }
    find(options) { return this.byOptions.get(key(options)) || null; }
    available(position, value, selected, colorPosition) {
      return this.variants.some(v => v.available && v.options[position] === value &&
        (position === colorPosition || selected.every((s, i) => i === position || v.options[i] === s)));
    }
    main(variant, selected, colorPosition, fallback) {
      const peers = colorPosition < 0 ? [] : this.variants.filter(v => v.options[colorPosition] === selected[colorPosition]);
      return String(variant?.mediaId || peers.find(v => v.mediaId)?.mediaId || fallback || '');
    }
  }

  class ProductView extends HTMLElement {
    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('change', e => {
          if (e.target.matches('[data-option-id]') && this.ready && !this.submitting) this.selectVariant();
        });
        this.addEventListener('submit', e => { if (e.target.matches('[data-product-form]')) this.add(e); });
        this.addEventListener('click', e => { if (e.target.closest('[data-pdp-retry]')) this.load(); });
      }
      this.fields = [...this.querySelectorAll('[data-option-position]')].sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      this.colorPosition = this.fields.findIndex(f => [normalized(this.dataset.colorOption), 'color', 'colour'].filter(Boolean).includes(normalized(f.dataset.optionName)));
      this.sizePosition = this.fields.findIndex(f => [normalized(this.dataset.sizeOption), 'size'].filter(Boolean).includes(normalized(f.dataset.optionName)));
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.form?.querySelector('[name="id"]');
      this.button = this.querySelector('.add-button');
      this.status = this.querySelector('[data-pdp-status]');
      this.error = this.querySelector('[data-product-error]');
      this.retry = this.querySelector('[data-pdp-retry]');
      this.gallery = this.querySelector('[data-pdp-gallery]');
      if (!this.form || !this.idInput || !this.gallery || !this.status) return;
      this.initialId = this.idInput.value;
      this.onPop = () => this.restoreURL();
      window.addEventListener('popstate', this.onPop);
      this.load();
    }
    disconnectedCallback() {
      this.loadVersion = (this.loadVersion || 0) + 1;
      this.controller?.abort();
      window.removeEventListener('popstate', this.onPop);
    }
    selection() {
      if (!this.fields.length) return this.map?.variants[0]?.options || [];
      return this.fields.map(f => f.querySelector('[data-option-id]:checked')?.value || '');
    }
    setSelection(options) {
      this.fields.forEach((f, i) => f.querySelectorAll('[data-option-id]').forEach(input => { input.checked = input.value === options[i]; }));
    }
    lock(locked) { this.fields.forEach(f => { f.disabled = locked; }); }
    dataURL(page = 1, param = 'page') {
      // Preserve theme preview/market parameters, but never submit an old variant or filter to the data endpoint.
      const url = new URL(this.dataset.url, location.origin);
      const current = new URL(location.href);
      for (const name of ['preview_theme_id', '_fd', 'pb', 'country', 'currency']) {
        if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
      }
      url.searchParams.delete('variant');
      url.searchParams.delete('option_values');
      url.searchParams.set('section_id', 'pdp-variant-data');
      url.searchParams.set(param, String(page));
      return url;
    }
    async page(page, param, signal) {
      const response = await fetch(this.dataURL(page, param), { signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw Error('Could not load options');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = doc.querySelector('[data-pdp-variant-page]');
      if (!script) throw Error('Missing variant data section');
      const data = JSON.parse(script.textContent);
      if (String(data.productId) !== this.dataset.productId || data.page !== page ||
          data.total !== Number(this.dataset.variantCount) || !Array.isArray(data.variants) ||
          !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100 ||
          typeof data.pageParam !== 'string') throw Error('Incomplete variant data');
      return data;
    }
    async load() {
      if (this.submitting) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const version = this.loadVersion = (this.loadVersion || 0) + 1;
      const timeout = setTimeout(() => controller.abort(), 30000);
      this.ready = false;
      this.lock(true);
      this.idInput.disabled = true;
      this.button.disabled = true;
      this.button.textContent = 'Loading options…';
      this.status.textContent = 'Loading options…';
      this.retry.hidden = true;
      this.error.textContent = '';
      this.gallery.setAttribute('aria-busy', 'true');
      try {
        const first = await this.page(1, 'page', controller.signal);
        const pages = new Array(first.pages);
        pages[0] = first;
        let next = 2;
        const worker = async () => {
          while (next <= first.pages) {
            const index = next++;
            const data = await this.page(index, first.pageParam, controller.signal);
            if (data.pages !== first.pages || data.pageParam !== first.pageParam || data.currency !== first.currency) throw Error('Variant data changed while loading');
            pages[index - 1] = data;
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, first.pages - 1) }, worker));
        if (version !== this.loadVersion || !this.isConnected) return;
        const variants = pages.flatMap(p => p.variants);
        this.map = new VariantMap(variants, first.total, this.fields.length || 1);
        this.templates = new Map([...this.querySelectorAll('template[data-pdp-media]')].map(t => [t.dataset.pdpMedia, t]));
        this.ready = true;
        const requested = new URL(location.href).searchParams.get('variant');
        const start = requested ? this.map.byId.get(requested) : this.map.byId.get(this.initialId);
        if (start) this.setSelection(start.options);
        this.lock(false);
        this.selectVariant(false);
      } catch (error) {
        if (version !== this.loadVersion || !this.isConnected) return;
        controller.abort();
        this.ready = false;
        this.button.textContent = 'Options not loaded';
        this.status.textContent = '';
        this.error.textContent = 'We could not load all dress options. Please retry before adding to your bag.';
        this.retry.hidden = false;
        this.gallery.setAttribute('aria-busy', 'false');
      } finally { clearTimeout(timeout); }
    }
    selectVariant(updateURL = true) {
      if (!this.ready) return;
      const selected = this.selection();
      this.selected = this.map.find(selected);
      this.fields.forEach((field, position) => {
        field.querySelector('[data-selected-option]').textContent = selected[position];
        field.querySelectorAll('[data-option-id]').forEach(input => {
          const available = this.map.available(position, input.value, selected, this.colorPosition);
          const label = input.closest('.option-label');
          const note = label.querySelector('[data-option-status]');
          label.classList.toggle('unavailable', !available);
          // Keep radios operable: a sold-out combination must not trap the buyer or block a color change.
          note.hidden = available;
          note.textContent = available ? '' : 'Unavailable';
        });
      });
      this.idInput.value = this.selected?.id || '';
      this.idInput.disabled = !this.selected;
      const price = this.querySelector('[data-product-price]');
      price.replaceChildren();
      if (this.selected) {
        const current = document.createElement('span');
        current.className = 'price';
        current.textContent = this.selected.priceText;
        price.append(current);
        if (this.selected.compareAt > this.selected.price) {
          const compare = document.createElement('s');
          compare.textContent = this.selected.compareText;
          price.append(' ', compare);
        }
      }
      const custom = this.querySelector('[data-custom-fields]');
      if (custom) {
        const active = this.sizePosition >= 0 && normalized(selected[this.sizePosition]) === normalized(this.dataset.customSize);
        custom.hidden = !active;
        custom.disabled = !active;
      }
      this.syncButton();
      this.status.textContent = this.selected ? (this.selected.available ? '' : 'This combination is sold out.') : 'This combination is not available. Please choose another option.';
      this.error.textContent = '';
      this.syncGallery(selected);
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.delete('option_values');
        if (this.selected) url.searchParams.set('variant', this.selected.id);
        else {
          url.searchParams.delete('variant');
          const ids = this.fields.map(f => f.querySelector('[data-option-id]:checked')?.dataset.optionId || '');
          if (ids.length && ids.every(Boolean)) url.searchParams.set('option_values', ids.join(','));
        }
        history.replaceState(history.state, '', url);
      }
    }
    syncButton() {
      this.button.disabled = !this.ready || this.submitting || !this.selected?.available;
      this.button.textContent = this.submitting ? 'Adding…' : !this.selected ? 'Unavailable' : this.selected.available ? 'Add to bag' : 'Sold out';
    }
    syncGallery(selected) {
      const color = this.colorPosition < 0 ? '' : selected[this.colorPosition];
      const hasColorMain = this.colorPosition < 0 || this.map.variants.some(variant =>
        variant.options[this.colorPosition] === color && variant.mediaId);
      if (hasColorMain) delete this.gallery.dataset.qa;
      else {
        this.gallery.dataset.qa = 'MISSING_COLOR_MAIN';
        this.warnedMissingColors ||= new Set();
        if (!this.warnedMissingColors.has(color)) {
          console.warn('MISSING_COLOR_MAIN', { productId: this.dataset.productId, color });
          this.warnedMissingColors.add(color);
        }
      }
      let main = this.map.main(this.selected, selected, this.colorPosition, this.gallery.dataset.defaultMedia);
      if (!this.templates.has(main)) main = this.gallery.dataset.defaultMedia;
      const shared = [...this.templates.keys()].filter(id => !this.map.mainIds.has(id));
      this.gallery.dataset.colorMainCount = String(this.map.mainIds.size);
      this.gallery.dataset.sharedCount = String(shared.length);
      const ids = [...new Set([main, ...shared])].filter(id => this.templates.has(id));
      const signature = JSON.stringify(ids);
      if (signature !== this.gallerySignature) {
        const nodes = ids.map((id, index) => {
          const node = this.templates.get(id).content.firstElementChild.cloneNode(true);
          node.className = index ? 'gallery-secondary' : 'gallery-primary';
          if (!index) {
            node.setAttribute('data-primary-media', '');
            const img = node.querySelector('img');
            if (img) img.loading = 'eager';
          }
          return node;
        });
        this.gallery.querySelectorAll('video').forEach(video => video.pause());
        this.gallery.replaceChildren(...nodes);
        this.gallery.scrollLeft = 0;
        this.gallery.dataset.activeIndex = '0';
        this.gallery.dataset.mediaCount = String(ids.length);
        this.gallerySignature = signature;
      }
      this.gallery.setAttribute('aria-busy', 'false');
    }
    restoreURL() {
      if (!this.ready || this.submitting) return;
      const url = new URL(location.href);
      const variant = this.map.byId.get(url.searchParams.get('variant'));
      if (variant) this.setSelection(variant.options);
      else if (url.searchParams.has('option_values')) {
        const ids = url.searchParams.get('option_values').split(',');
        this.fields.forEach((f, i) => f.querySelectorAll('[data-option-id]').forEach(input => { input.checked = input.dataset.optionId === ids[i]; }));
      } else {
        const initial = this.map.byId.get(this.initialId);
        if (initial) this.setSelection(initial.options);
      }
      this.selectVariant(false);
    }
    async add(event) {
      event.preventDefault();
      if (!this.ready || this.submitting || !this.selected?.available || this.idInput.value !== String(this.selected.id) || !this.form.reportValidity()) return;
      this.submitting = true;
      this.lock(true);
      this.syncButton();
      this.error.textContent = '';
      try {
        const data = new FormData(this.form);
        data.set('id', this.selected.id);
        data.append('sections', 'cart-drawer');
        data.append('sections_url', location.pathname);
        const response = await fetch(window.DressTheme.routes.add, { method: 'POST', body: data });
        const result = await response.json();
        if (!response.ok) throw Error(result.description || 'Unable to add this item.');
        try { await window.DressTheme.updateCart(result.sections); window.DressTheme.openCart(); }
        catch { this.error.textContent = 'Your dress was added. Open your bag to review it before adding again.'; }
      } catch (error) { this.error.textContent = error.message || 'Unable to add this item. Please check your bag before trying again.'; }
      finally { this.submitting = false; this.lock(false); this.syncButton(); }
    }
  }
  customElements.define('product-view', ProductView);
})();
